import { Component, ContentChildren, Input, QueryList } from "@angular/core";
import { v4 } from "uuid";

import { TabTitleDirective } from "../../directives/tab-title.directive";

/**
 * This component implements a TabItem Component. It has to be used with TabListComponent
 *
 * It manages accessibility attributes for you, according Tabs design pattern (@see https://www.w3.org/TR/wai-aria-practices/#tabpanel)
 *
 * You can use [hTabTitle] structural directive to set a custom title content.
 *
 * When using the hTabTitle directive, you can retrieve context information (see https://angular.io/api/common/NgTemplateOutlet).
 *
 * Usage:
 * ```
 * <h-tab-list defaultSelectedTab="1" ariaLabel="name-of-tablist" backgroundColor="red"
 *            (selectedChange)="onTabChanged($event)">
 *
 *  <h-tab-item tabTitle="My Title" i18n-tabTitle="@@hermes.checkout.returning.customer" tabId="my-tab-id" [active]='true'>
 *
 *      <p>Tab Content</p>
 *
 *  </h-tab-item>
 *
 *  <h-tab-item tabId="my-second-tab-id" [active]='true'>
 *
 *    <ng-template [hTabTitle]>
 *      <p>Custom Title Content</p>
 *    </ng-template>
 *
 *    <p>Tab Content</p>
 *
 *  </h-tab-item>
 *
 * </h-tab-list>
 * ```
 */
@Component({
    selector: "h-tab-item",
    templateUrl: "./tab-item.component.html",
    standalone: true,
})
export class TabItemComponent {
    /**
     * Observe form the content DOM if this component has custom tab table template to display
     */
    @ContentChildren(TabTitleDirective)
    public customTabTitle: QueryList<TabTitleDirective> =
        new QueryList<TabTitleDirective>();

    /**
     * Required Input if there is no custom tab table template use
     */
    @Input()
    public tabTitle: string | undefined;

    /**
     * Optional Input to active or not the tab
     */
    @Input()
    public active = true;

    /**
     * Unique id for this tab.
     * Can be overriden.
     */
    @Input()
    public tabId: string = v4();

    /**
     * Internal property use to display or not the tab content
     */
    public display: boolean = false;
}
